<template>
	<v-row :key="key" no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- Time tracking of the user in this page -->
		<UserTimeTracker page-key="ccmLabs" />

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="dataLoading">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('ccm-labs') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<!-- <v-tooltip left>
								<template #activator="{ on, attrs }">
									<v-icon
										v-on="on"
										v-bind="attrs"
										color="primary"
										:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
										@click="showUploadFIleDialog = true">
										mdi-creation
									</v-icon>
								</template>
								{{ $t('upload-lab-using-ai') }}
							</v-tooltip> -->

							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloading" />

							<v-icon
								color="primary"
								:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
								@click="synchronizeAddDrawers('ccmLabsAdd')">
								mdi-plus-circle
							</v-icon>
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="labs"
						:headers="headers"
						:items-per-page="-1"
						style="overflow-y: auto"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						sort-by="reviewedAt"
						sort-desc
						class="data-table-outline"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, index }">
							<tr :class="getRowsForDataTable(index)" @click.stop="openViewDialog(item)">
								<td class="text-left px-1">
									{{ item.testName }}
								</td>
								<td class="text-left px-1">
									{{ item.specimenType }}
								</td>

								<td class="text-left px-1">
									{{ item.value }}
								</td>
								<td class="text-left px-1">
									{{ item.unit }}
								</td>

								<td class="text-left px-1">
									{{ item.interpretation }}
								</td>

								<td class="text-left px-1">
									<v-icon color="primary" size="20">
										{{ item.isCritical ? 'mdi-checkbox-blank' : 'mdi-checkbox-blank-outline' }}
									</v-icon>
								</td>

								<td class="text-left px-1">
									{{ formatTimestamp(item.reviewedAt) }}
								</td>
								<td class="text-left px-1">
									{{ formatDuration(item.timeSpentSeconds) }}
								</td>
								<td class="px-1 text-right">
									<span v-if="item.ccmRecordId === selectedCCMRecord.id">
										<ActionButton
											icon="mdi-delete"
											tooltip="delete"
											:maxSize="25"
											:minSize="20"
											@clicked="
												deleteId = item.id;
												showDeleteDialog = true;
											"
											:busy="deletingIds.includes(item.id)" />

										<v-tooltip left>
											<template v-slot:activator="{ on }">
												<v-icon
													v-on="on"
													:size="$vuetify.breakpoint.mdAndDown ? 20 : 25"
													color="primary"
													@click.stop="openEditDrawer(item)">
													mdi-pencil
												</v-icon>
											</template>
											{{ $t('edit') }}
										</v-tooltip>
									</span>
								</td>
							</tr>
						</template>
						<template v-slot:[`footer.prepend`]>
							<v-row dense class="ma-0">
								<v-col class="text-left">
									<p class="ma-0">
										<v-tooltip top>
											<template v-slot:activator="{ on }">
												<v-icon class="ml-1" color="primary" size="25" v-on="on"> mdi-help-circle-outline </v-icon>
											</template>
											{{ $t('need-to-select-the-correct-record') }}
										</v-tooltip>
									</p>
								</v-col>
							</v-row>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>

		<UploadLabUsingAiDialog
			:showDialog.sync="showUploadFIleDialog"
			v-if="showUploadFIleDialog"
			@closeDialog="showUploadFIleDialog = false" />

		<CCMLabsViewDialog
			:showDialog.sync="showViewDialog"
			v-if="showViewDialog && selectedLab"
			:selectedLab="selectedLab"
			@closeDialog="showViewDialog = false" />

		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteLab(deleteId);
				deleteId = -1;
			" />
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	getRowsForDataTable,
	formatTimestamp,
	formatDuration,
	downloadCSVWithKeys,
	getFullName,
} from '@/utils/utilities';
import CCMLabsViewDialog from '../../../dialogs/CCMLabsViewDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import UserTimeTracker from '@/views/Client/components/UserTimeTracker.vue';
import ActionButton from '../ActionButton.vue';
import UploadLabUsingAiDialog from '../../../dialogs/UploadLabUsingAiDialog.vue';

export default {
	name: 'CCMLabs',
	components: { CCMLabsViewDialog, DeleteDialog, UserTimeTracker, ActionButton, UploadLabUsingAiDialog },

	data () {
		return {
			key: 0, // Used to force re-rendering of the component
			dataLoading: true,
			deleteId: -1,
			deletingIds: [],
			selectedLab: null,
			showDeleteDialog: false,
			showViewDialog: false,
			downloading: false,
			showUploadFIleDialog: false,
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			labs: (state) => state.ccmLabs.ccmLabsByPatientId,
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{
					text: this.$t('name'),
					sortable: true,
					value: 'testName',
					class: 'px-1',
				},
				{
					text: this.$t('specimen-type'),
					sortable: true,
					value: 'specimenType',
					class: 'px-1',
				},
				{
					text: this.$t('value'),
					sortable: false,
					value: 'value',
					class: 'px-1',
				},
				{
					text: this.$t('unit'),
					sortable: false,
					value: 'unit',
					class: 'px-1',
				},
				{
					text: this.$t('interpretation'),
					sortable: false,
					value: 'interpretation',
					class: 'px-1',
				},
				{
					text: this.$t('is-critical'),
					sortable: true,
					value: 'isCritical',
					class: 'px-1',
				},
				{
					text: this.$t('review-date'),
					sortable: true,
					value: 'reviewedAt',
					class: 'px-1',
				},

				{
					text: this.$t('time-spent-header'),
					sortable: true,
					value: 'timeSpentSeconds',
					class: 'px-1',
				},
				{
					text: this.$t('actions'),
					sortable: false,
					value: '',
					align: 'right',
					class: 'pl-1 pr-3',
				},
			];
		},
	},

	methods: {
		...mapActions({
			getCCMLabs: 'ccmLabs/getCCMLabsByPatientId',
			deleteCCMLab: 'ccmLabs/deleteCCMLab',
			uploadLabDocumentToAi: 'ccmLabs/uploadLabDocumentToAi',
		}),

		getRowsForDataTable,
		formatTimestamp,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		async deleteLab (id) {
			await this.deleteCCMLab(id);
			this.deletingIds = this.deletingIds.filter((item) => item !== id);
		},

		async openEditDrawer (lab) {
			this.$store.commit('ccmLabs/STORE_CCM_LAB', lab);
			this.synchronizeEditDrawers('ccmLabsEdit');
		},
		openViewDialog (lab) {
			this.selectedLab = lab;
			this.showViewDialog = true;
		},
		async downloadCSV () {
			this.downloading = true;
			await downloadCSVWithKeys(this.labs, 'Labs_for_' + getFullName(this.detailedUser.userData), {
				ids: ['ccmRecordId'],
				data: [
					'testName',
					'relatedDiagnosisCode',
					'value',
					'unit',
					'interpretation',
					'isCritical',
					'panelGroup',
					'specimenType',
					'performedBy',
					'performedByRole',
					'reviewedBy',
				],
				timestamps: ['labDate', 'reviewedAt'],
				timeSpent: ['timeSpentSeconds'],
			});
			this.downloading = false;
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		synchronizeEditDrawers (drawer) {
			this.$store.commit('SET_PATIENT_DETAILS_DRAWER', drawer);
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', {
				updateDrawer: true,
				page2: 'ccmDetails',
			});
		},
	},

	watch: {
		selectedCCMRecord () {
			this.key = !this.key;
		},
	},

	async mounted () {
		await this.getCCMLabs(this.detailedUser.userData.id);
		this.dataLoading = false;
	},
};
</script>
